import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItemButton,
    ListItemText,
    CircularProgress,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { getTemplates } from '../redux/ducks/template';
import { useHasPermission } from '../utils/permissionUtils';

const HelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const SearchField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4, 2),
    textAlign: 'center',
}));

const ScrollList = styled(List)(({ theme }) => ({
    maxHeight: 320,
    overflowY: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const OptionRow = styled(ListItemButton, { shouldForwardProp: (prop) => prop !== '$selected' })(({ theme, $selected }) => ({
    alignItems: 'flex-start',
    backgroundColor: $selected ? theme.palette.action.selected : 'transparent',
}));

const StepCount = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
}));

const stepCountOf = (template) => (Array.isArray(template?.items) ? template.items.length : 0);

const ChecklistTemplatePickerDialog = ({ open, onClose, onImport, importing = false, existingItemCount = 0 }) => {
    const dispatch = useDispatch();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [selectedId, setSelectedId] = useState('');
    const [search, setSearch] = useState('');
    const hasPermission = useHasPermission();
    const canViewTemplates = hasPermission('viewTemplates');

    const load = useCallback(() => {
        if (!canViewTemplates) return;
        setLoading(true);
        setLoadFailed(false);
        dispatch(
            getTemplates('checklist', 0, 200, (data) => {
                setLoading(false);
                if (!data) {
                    setLoadFailed(true);
                    setTemplates([]);
                    return;
                }
                setTemplates((Array.isArray(data) ? data : []).filter((t) => t.templateType === 'checklist'));
            })
        );
    }, [canViewTemplates, dispatch]);

    useEffect(() => {
        if (!open) return;
        setSelectedId('');
        setSearch('');
        load();
    }, [open, load]);

    const term = search.trim().toLowerCase();
    const visible = term ? templates.filter((t) => t.name?.toLowerCase().includes(term) || t.description?.toLowerCase().includes(term)) : templates;

    const handleConfirm = () => {
        if (!selectedId) return;
        onImport(selectedId);
    };

    const renderBody = () => {
        if (!canViewTemplates) {
            return (
                <StateBox>
                    <Typography variant="body2">You don't have access to templates.</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Close this window and add the steps by hand, or ask an administrator for template access.
                    </Typography>
                </StateBox>
            );
        }

        if (loading) {
            return (
                <StateBox>
                    <CircularProgress size={28} />
                    <Typography variant="body2" color="text.secondary">
                        Loading checklist templates...
                    </Typography>
                </StateBox>
            );
        }

        if (loadFailed) {
            return (
                <StateBox>
                    <Typography variant="body2">Checklist templates could not be loaded.</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Nothing has changed on this task. Try again, or close this window and carry on adding steps by hand.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={load}>
                        Try again
                    </Button>
                </StateBox>
            );
        }

        if (templates.length === 0) {
            return (
                <StateBox>
                    <Typography variant="body2">No checklist templates exist yet.</Typography>
                    <Typography variant="caption" color="text.secondary">
                        An administrator can create one on the Templates page.
                    </Typography>
                </StateBox>
            );
        }

        if (visible.length === 0) {
            return (
                <StateBox>
                    <Typography variant="body2">No checklist template matches "{search.trim()}".</Typography>
                </StateBox>
            );
        }

        return (
            <ScrollList disablePadding>
                {visible.map((template) => {
                    const count = stepCountOf(template);
                    return (
                        <OptionRow key={template._id} $selected={selectedId === template._id} onClick={() => setSelectedId(template._id)}>
                            <ListItemText
                                primary={template.name}
                                secondary={
                                    <>
                                        {template.description && (
                                            <Typography variant="body2" color="text.secondary" component="span" display="block">
                                                {template.description}
                                            </Typography>
                                        )}
                                        <StepCount variant="caption" component="span" display="block">
                                            {count} {count === 1 ? 'step' : 'steps'}
                                        </StepCount>
                                    </>
                                }
                            />
                        </OptionRow>
                    );
                })}
            </ScrollList>
        );
    };

    return (
        <Dialog open={open} onClose={importing ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>Import checklist from a template</DialogTitle>
            <DialogContent>
                <HelperText variant="body2">
                    {existingItemCount > 0
                        ? `The template's steps are added to the end of this checklist. Your ${existingItemCount} existing ${existingItemCount === 1 ? 'step stays' : 'steps stay'} exactly as ${existingItemCount === 1 ? 'it is' : 'they are'} — nothing is replaced or removed.`
                        : "The template's steps are copied onto this task. You can edit or remove them afterwards."}
                </HelperText>

                {templates.length > 0 && !loading && !loadFailed && (
                    <SearchField
                        fullWidth
                        size="small"
                        placeholder="Search checklist templates..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {renderBody()}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={importing}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleConfirm} disabled={!selectedId || importing}>
                    {importing ? 'Adding steps...' : 'Add these steps'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChecklistTemplatePickerDialog;
